import React from 'react';
import { Loader2 } from 'lucide-react';

export type ButtonVariant = 'primary' | 'solid' | 'ghost' | 'cta' | 'danger';
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  isLoading?: boolean;
  leadingIcon?: React.ReactNode;
  children?: React.ReactNode;
}

// Design system glass: sentence case (sem caixa alta), 600, raio 12/14, degradê de
// marca só na ação primária, "secundário" em vidro de controle. Transições curtas em
// transform/sombra, sem bounce.
const motion = 'transition-[transform,box-shadow,opacity,background-color,border-color] duration-150 ease-standard';
const focus = 'focus-visible:outline-none focus-visible:ring-snapfy-focus';
const disabledCls = 'disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:translate-y-0 disabled:active:scale-100';

const variantClasses: Record<ButtonVariant, string> = {
  // Ação primária — degradê magenta→lilás→índigo com brilho de CTA.
  primary: [
    'bg-snapfy-gradient text-white font-semibold text-[13px]',
    'px-[18px] py-2.5 rounded-button shadow-cta',
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    motion,
    'hover:-translate-y-px hover:shadow-cta-lg',
    'active:translate-y-0 active:scale-[0.98]',
    focus,
    disabledCls,
  ].join(' '),

  // Sólido índigo (sem equivalente no DS: mantém a forma, só a cor muda).
  solid: [
    'bg-snapfy-primary text-white font-semibold text-[13px]',
    'px-5 py-2.5 rounded-input',
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    motion,
    'hover:bg-[var(--snapfy-primary-hover)]',
    'active:scale-[0.98]',
    focus,
    'disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100',
  ].join(' '),

  // CTA grande das ferramentas (Gerar/Analisar/Executar) — tamanho "cta" do DS: 14px/600, raio 14.
  cta: [
    'bg-snapfy-gradient text-white font-semibold text-sm',
    'px-5 py-3 rounded-[14px] shadow-cta-lg',
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    motion,
    'hover:-translate-y-px',
    'active:translate-y-0 active:scale-[0.98]',
    focus,
    disabledCls,
  ].join(' '),

  // Destrutivo — sem equivalente no DS: mantém a forma, cor de erro.
  danger: [
    'bg-snapfy-error text-white font-semibold text-[13px]',
    'px-5 py-2.5 rounded-input',
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    motion,
    'hover:opacity-90',
    'active:scale-[0.98]',
    focus,
    'disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100',
  ].join(' '),

  // Secundário do DS — vidro de controle com borda de campo.
  ghost: [
    'bg-snapfy-control border border-snapfy-border-field text-snapfy-text-primary backdrop-blur-[8px]',
    'font-semibold text-[13px]',
    'px-4 py-2.5 rounded-button',
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    motion,
    'hover:bg-snapfy-raised hover:border-snapfy-border-strong',
    'active:scale-[0.98]',
    focus,
    disabledCls,
  ].join(' '),
};

// Tamanho "sm" do DS: 30px de altura, 12px/500, raio 10.
const smVariantClasses: Partial<Record<ButtonVariant, string>> = {
  primary: [
    'bg-snapfy-gradient text-white font-medium text-xs',
    'px-3 py-2 rounded-control shadow-cta',
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap',
    motion,
    'hover:-translate-y-px',
    'active:translate-y-0 active:scale-[0.98]',
    focus,
    disabledCls,
  ].join(' '),

  cta: [
    'bg-snapfy-gradient text-white font-medium text-xs',
    'px-3 py-2 rounded-control shadow-cta',
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap',
    motion,
    'hover:-translate-y-px',
    'active:translate-y-0 active:scale-[0.98]',
    focus,
    disabledCls,
  ].join(' '),

  solid: [
    'bg-snapfy-primary text-white font-medium text-xs',
    'px-3 py-2 rounded-control',
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap',
    motion,
    'hover:bg-[var(--snapfy-primary-hover)]',
    'active:scale-[0.98]',
    focus,
    'disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100',
  ].join(' '),

  danger: [
    'bg-snapfy-error text-white font-medium text-xs',
    'px-3 py-2 rounded-control',
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap',
    motion,
    'hover:opacity-90',
    'active:scale-[0.98]',
    focus,
    'disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100',
  ].join(' '),

  ghost: [
    'bg-snapfy-control border border-snapfy-border-field text-snapfy-text-primary backdrop-blur-[8px]',
    'font-medium text-xs',
    'px-3 py-2 rounded-control',
    'inline-flex items-center justify-center gap-1.5 whitespace-nowrap',
    motion,
    'hover:bg-snapfy-raised hover:border-snapfy-border-strong',
    'active:scale-[0.98]',
    focus,
    disabledCls,
  ].join(' '),
};

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  isLoading = false,
  leadingIcon,
  children,
  disabled,
  className = '',
  ...rest
}) => {
  const isDisabled = disabled || isLoading;
  const resolvedClasses = size === 'sm' ? smVariantClasses[variant] ?? variantClasses[variant] : variantClasses[variant];

  return (
    <button
      {...rest}
      disabled={isDisabled}
      aria-busy={isLoading ? true : undefined}
      className={`${resolvedClasses} ${className}`.trim()}
    >
      {isLoading ? (
        <>
          <Loader2
            className="animate-spin motion-reduce:animate-none"
            size={16}
            aria-hidden="true"
          />
          {children}
        </>
      ) : (
        <>
          {leadingIcon && <span aria-hidden="true">{leadingIcon}</span>}
          {children}
        </>
      )}
    </button>
  );
};

export default Button;
